import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useState } from 'react'
import { Gift, Plus, Percent, Tag } from 'lucide-react'
import clsx from 'clsx'

interface Reward {
  id: string
  partner_name: string
  partner_logo_url: string | null
  discount_type: string
  discount_value: number
  discount_description: string
  category: string
  is_active: boolean
  usage_count: number
  usage_limit: number | null
}

const categoryColors: Record<string, string> = {
  restaurant: 'bg-orange-100 text-orange-700',
  grocery: 'bg-green-100 text-green-700',
  cafe: 'bg-amber-100 text-amber-700',
  health: 'bg-blue-100 text-blue-700'
}

export default function Rewards() {
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    partner_name: '',
    discount_type: 'percentage',
    discount_value: '',
    discount_description: '',
    category: 'restaurant'
  })

  const { data: rewards, isLoading } = useQuery({
    queryKey: ['rewards'],
    queryFn: () => api.get<Reward[]>('/rewards')
  })

  const addReward = useMutation({
    mutationFn: (data: typeof form) => api.post('/rewards', {
      ...data,
      discount_value: parseInt(data.discount_value)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards'] })
      setShowAdd(false)
      setForm({ partner_name: '', discount_type: 'percentage', discount_value: '', discount_description: '', category: 'restaurant' })
    }
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(`/rewards/${id}`, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rewards'] })
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Partner Rewards</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-salad-600 text-white px-4 py-2 rounded-lg hover:bg-salad-700"
        >
          <Plus size={20} />
          Add Reward
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Add Partner Reward</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Partner Name"
              value={form.partner_name}
              onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
              className="px-4 py-2 border rounded-lg"
            />
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="restaurant">Restaurant</option>
              <option value="grocery">Grocery</option>
              <option value="cafe">Cafe</option>
              <option value="health">Health</option>
            </select>
            <select
              value={form.discount_type}
              onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="percentage">Percentage Off</option>
              <option value="fixed">Fixed Amount Off</option>
              <option value="cashback">Cashback</option>
            </select>
            <input
              type="number"
              placeholder={form.discount_type === 'percentage' ? 'Percentage (e.g. 10)' : 'Amount in pence'}
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              className="px-4 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Description (e.g. 10% off all salads)"
              value={form.discount_description}
              onChange={(e) => setForm({ ...form, discount_description: e.target.value })}
              className="px-4 py-2 border rounded-lg md:col-span-2"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => addReward.mutate(form)}
              disabled={!form.partner_name || !form.discount_value}
              className="bg-salad-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              Add Reward
            </button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-gray-600">
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-200 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rewards?.map(reward => (
            <div key={reward.id} className={clsx(
              'bg-white rounded-xl p-4 shadow-sm border-2',
              reward.is_active ? 'border-transparent' : 'border-gray-200 opacity-60'
            )}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-salad-100 rounded-lg flex items-center justify-center">
                    <Gift className="text-salad-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{reward.partner_name}</h3>
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', categoryColors[reward.category])}>
                      {reward.category}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => toggleActive.mutate({ id: reward.id, is_active: !reward.is_active })}
                  className={clsx(
                    'px-3 py-1 rounded-full text-sm font-medium',
                    reward.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {reward.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
              <div className="mt-4 flex items-center gap-2">
                {reward.discount_type === 'percentage' ? (
                  <Percent className="text-salad-600" size={18} />
                ) : (
                  <Tag className="text-salad-600" size={18} />
                )}
                <span className="font-medium">
                  {reward.discount_type === 'percentage' 
                    ? `${reward.discount_value}% off`
                    : `£${(reward.discount_value / 100).toFixed(2)} off`}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">{reward.discount_description}</p>
              <div className="text-xs text-gray-400 mt-2">
                {reward.usage_count} uses {reward.usage_limit && `/ ${reward.usage_limit} limit`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
